import React, { useEffect, useState } from 'react';

import { BiGrid, BiBarChart } from 'react-icons/bi';
import BloodPressureChart from '../Charts/BloodPressureChart';
import BodyTemperatureChart from '../Charts/BodyTemperatureChart';
import CalorieChart from '../Charts/CalorieChart';
import HeartRateChart from '../Charts/HeartRateChart';
import SleepChart from '../Charts/SleepChart';
import StepChart from '../Charts/StepChart';
import RespiratoryRateChart from '../Charts/RespiratoryrateChart';
import BloodOxygenationChart from '../Charts/BloodOxygenationChart';

import { Container, ViewContent, ViewOptions } from './styles';
import { MetricTypeEnum } from '../../../../../../../enums/MetricTypeEnum';
import { DateRangeType, OptionsType } from '..';
import EvolutionTable from '../../../EvolutionTable';
import { EvolutionMinMax } from '../../../EvolutionMinMax';

export interface ChartsProps {
  dateRange: DateRangeType;
}
export interface ContentChartProps {
  metric: MetricTypeEnum;
  options: OptionsType[];
  selectedDateOption: OptionsType;
  updateDateOption: React.Dispatch<React.SetStateAction<OptionsType>>;
}

type OptionsChartsType = {
  [key in MetricTypeEnum]?: JSX.Element;
};

interface ViewOptionsProps {
  screen: 'table' | 'chart';
  icon: JSX.Element;
  key: string;
  name: string;
}

type MetricMeasurementMap = {
  [key in MetricTypeEnum]: string;
};

const MetricView: React.FC<ContentChartProps> = ({
  metric,
  options,
  selectedDateOption,
  updateDateOption,
}) => {
  const optionsCharts: OptionsChartsType = {
    bloodPressure: (
      <BloodPressureChart dateRange={selectedDateOption.rangeDate} />
    ),
    bodyTemperature: (
      <BodyTemperatureChart dateRange={selectedDateOption.rangeDate} />
    ),
    bloodOxygen: (
      <BloodOxygenationChart dateRange={selectedDateOption.rangeDate} />
    ),
    respiratoryRate: (
      <RespiratoryRateChart dateRange={selectedDateOption.rangeDate} />
    ),
    calories: <CalorieChart dateRange={selectedDateOption.rangeDate} />,
    heartBeatRate: <HeartRateChart dateRange={selectedDateOption.rangeDate} />,
    sleep: <SleepChart dateRange={selectedDateOption.rangeDate} />,
    steps: <StepChart dateRange={selectedDateOption.rangeDate} />,
  };

  const viewOptions: ViewOptionsProps[] = [
    {
      screen: 'chart',
      name: 'Gráficos',
      icon: <BiBarChart />,
      key: 'charts',
    },
    {
      screen: 'table',
      name: 'Tabelas',
      icon: <BiGrid />,
      key: 'table',
    },
  ];

  const [viewOption, setViewOption] = useState<ViewOptionsProps>(
    viewOptions[0],
  );

  const disabledOptions =
    metric === MetricTypeEnum.CALORIES ||
    metric === MetricTypeEnum.SLEEP ||
    metric === MetricTypeEnum.STEPS;

  useEffect(() => {
    if (disabledOptions) {
      setViewOption(viewOptions[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metric]);

  const MetricMeasurement: MetricMeasurementMap = {
    [MetricTypeEnum.HEART_BEAT_RATE]: 'bpm',
    [MetricTypeEnum.BLOOD_PREASSURE]: 'mmHg',
    [MetricTypeEnum.BODY_TEMPERATURE]: 'ºC',
    [MetricTypeEnum.BLOOD_OXYGEN]: '%',
    [MetricTypeEnum.SLEEP]: '',
    [MetricTypeEnum.STEPS]: '',
    [MetricTypeEnum.CALORIES]: 'kcal',
    [MetricTypeEnum.RESPIRATORY_RATE]: 'mrm',
    [MetricTypeEnum.GLUCOSE]: 'mg/dL',
  };

  return (
    <Container>
      <ViewOptions isMetricAvailable={disabledOptions}>
        {viewOptions.map((item) => (
          <button
            className={item.key}
            type="button"
            onClick={() => setViewOption(item)}
            key={item.key}
            disabled={item.key === viewOption.key || disabledOptions}
          >
            {item.icon}
          </button>
        ))}
        {viewOption.name}
      </ViewOptions>
      <ViewContent>
        {viewOption.screen === 'table' ? (
          <EvolutionTable
            metric={metric}
            dateRange={selectedDateOption.rangeDate}
          />
        ) : (
          optionsCharts[metric]
        )}
        <EvolutionMinMax
          rangeOptions={selectedDateOption.rangeDate}
          metric={metric}
          measure={MetricMeasurement[metric]}
        />
      </ViewContent>
    </Container>
  );
};

export default MetricView;
